import React, { useEffect, useState } from "react";
import { Select, Button, Space, message } from "antd";
import type { ClusterEnvItem } from "../api";

/**
 * 产线与环境选择组件 Props
 */
export interface EnvProductSelectorProps {
  /**
   * 集群环境原始数据
   */
  clusterEnvItems: ClusterEnvItem[];
  /**
   * 当前是否正在录制
   */
  isRecording: boolean;
  /**
   * 选中变化回调
   */
  onChange: (business: string | undefined, env: string | undefined) => void;
  /**
   * 开始录制
   */
  onStartRecording: () => void;
  /**
   * 显示历史
   */
  onShowHistory: () => void;
}

/**
 * 产线与环境选择组件
 * @param props - 组件属性
 * @returns React 元素
 */
const EnvProductSelector: React.FC<EnvProductSelectorProps> = (props) => {
  const {
    clusterEnvItems,
    isRecording,
    onChange,
    onStartRecording,
    onShowHistory,
  } = props;

  // 解析 business 和 business->env 映射
  function parseBusinessEnv(items: ClusterEnvItem[]) {
    const businessList: string[] = [];
    const businessEnvMap: Record<string, string[]> = {};
    items.forEach(item => {
      const business = (item as any).business ?? 'default';
      const env = (item as any).cluster_env ?? (item as any).clusterEnv;
      if (!businessEnvMap[business]) {
        businessEnvMap[business] = [];
        businessList.push(business);
      }
      if (env) businessEnvMap[business].push(env);
    });
    return { businessList, businessEnvMap };
  }

  // 内部状态
  const [{ businessList, businessEnvMap }, setBusinessData] = useState(() => parseBusinessEnv(clusterEnvItems));
  const [selectedBusiness, setSelectedBusiness] = useState<string | undefined>(undefined);
  const [envList, setEnvList] = useState<string[]>([]);
  const [selectedEnv, setSelectedEnv] = useState<string | undefined>(undefined);

  // 初始化/数据变化时重建映射
  useEffect(() => {
    const { businessList, businessEnvMap } = parseBusinessEnv(clusterEnvItems);
    setBusinessData({ businessList, businessEnvMap });
    if (businessList.length > 0) {
      setSelectedBusiness(businessList[0]);
      setEnvList(businessEnvMap[businessList[0]] || []);
      setSelectedEnv((businessEnvMap[businessList[0]] || [])[0]);
      onChange(businessList[0], (businessEnvMap[businessList[0]] || [])[0]);
    } else {
      setSelectedBusiness(undefined);
      setEnvList([]);
      setSelectedEnv(undefined);
      onChange(undefined, undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusterEnvItems]);

  // 产线选择变化
  const handleBusinessChange = (value: string) => {
    setSelectedBusiness(value);
    const envs = businessEnvMap[value] || [];
    setEnvList(envs);
    setSelectedEnv(envs[0] ?? undefined);
    onChange(value, envs[0] ?? undefined);
    message.info(`已选择产线: ${value}`);
  };

  // 环境选择变化
  const handleEnvChange = (value: string) => {
    setSelectedEnv(value);
    onChange(selectedBusiness, value);
  };

  return (
    <Space size="middle">
      {/* 产线选择框 */}
      <Select
        placeholder="产线"
        style={{ width: 120 }}
        value={selectedBusiness ?? undefined}
        onChange={handleBusinessChange}
        allowClear
      >
        {businessList.filter(Boolean).map((line) => (
          <Select.Option key={line} value={line}>
            {line}
          </Select.Option>
        ))}
      </Select>

      {/* 环境选择框 */}
      <Select
        placeholder="环境"
        style={{ width: 180 }}
        value={selectedEnv ?? undefined}
        onChange={handleEnvChange}
        allowClear
      >
        {envList.filter(Boolean).map((env) => (
          <Select.Option key={env} value={env}>
            {env}
          </Select.Option>
        ))}
      </Select>

      {isRecording ? (
        <Button type="primary" danger onClick={onShowHistory}>
          停止录制
        </Button>
      ) : (
        <Button type="primary" onClick={onStartRecording} disabled={!selectedEnv}>
          开始录制
        </Button>
      )}

      <Button style={{ borderColor: "#555555", color: "#ffffff" }} onClick={onShowHistory}>
        历史请求记录
      </Button>
    </Space>
  );
};

export default EnvProductSelector; 